import React, { useState } from 'react';
import { Contact } from '../../../types/contact.types';
import { FaArrowUp } from 'react-icons/fa';
import { Loader2, PlusCircle } from 'lucide-react';
import { useLeadScoring } from '../../../api/leadScore';
import { useToast } from '@crisellevaldez/speculo-ui';

interface CircularLeadScoreDisplayProps {
  contact: Contact;
  size?: 'sm' | 'md' | 'lg'; // Size variant of the component
  onScoreCalculated?: (updatedContact: Contact) => void; // Callback to update contact data with new score
}

export const CircularLeadScoreDisplay: React.FC<CircularLeadScoreDisplayProps> = ({
  contact,
  size = 'md', // Default to medium size
  onScoreCalculated,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const { scoreSelectedContact } = useLeadScoring();
  const { showToast } = useToast();

  // Function to trigger lead score calculation
  const handleCalculateScore = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isCalculating) return;

    try {
      setIsCalculating(true);
      const result = await scoreSelectedContact(contact.id, true);
      console.log('CircularLeadScoreDisplay - API Response:', result);
      showToast({
        message: 'Lead score calculated successfully',
        variant: 'success',
      });

      // Update the contact with the new score information
      if (onScoreCalculated && result && result.data) {
        const updatedContact = {
          ...contact,
          score: result.data.score,
          previous_score: contact.score || 0,
        };
        console.log('CircularLeadScoreDisplay - Updated Contact:', updatedContact);
        onScoreCalculated(updatedContact);
      }
    } catch (error) {
      console.error('Error calculating lead score:', error);
      showToast({
        message: 'Failed to calculate lead score',
        variant: 'error',
      });
    } finally {
      setIsCalculating(false);
    }
  };

  // Get the score from the contact, default to 0 if not present
  // Cap the score at 99 if it's greater than 99
  const score = typeof contact.score === 'number' ? Math.min(contact.score, 99) : 0;

  // Determine color based on score
  const getScoreColor = (score: number) => {
    if (score >= 50) return 'text-green-500';
    if (score >= 25) return 'text-amber-500';
    return score > 0 ? 'text-red-500' : 'text-blue-500';
  };

  const getBorderColor = (score: number) => {
    if (score >= 50) return 'border-green-500';
    if (score >= 25) return 'border-amber-500';
    return score > 0 ? 'border-red-500' : 'border-blue-500';
  };

  const scoreColor = getScoreColor(score);
  const borderColor = getBorderColor(score);

  // Size classes with responsive variants
  const sizeClasses = {
    sm: {
      circle: 'w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-[8px] sm:text-[9px] md:text-[10px]',
      arrow: 'w-1.5 h-1.5 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2',
      icon: 'w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3',
    },
    md: {
      circle: 'w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-[9px] sm:text-[10px] md:text-xs',
      arrow: 'w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5',
      icon: 'w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4',
    },
    lg: {
      circle: 'w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 text-[10px] sm:text-xs md:text-sm',
      arrow: 'w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3',
      icon: 'w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5',
    },
  };

  // Check if score has increased compared to previous score
  // Cap the previous score at 99 if it's greater than 99
  const previousScore =
    typeof contact.previous_score === 'number' ? Math.min(contact.previous_score, 99) : 0;
  const hasIncreased = previousScore > 0 && score > previousScore;

  // Format the hover text
  const getHoverText = () => {
    if (!score) return 'Click to calculate lead score';

    if (hasIncreased) {
      return `Lead Score improved from ${previousScore}%. Click to recalculate.`;
    }

    if (previousScore > 0) {
      return `Current lead score: ${score}% (previous: ${previousScore}%). Click to recalculate.`;
    }

    return `Current lead score: ${score}%. Click to recalculate.`;
  };

  return (
    <div className="relative">
      {/* Base display */}
      <div
        className={`${sizeClasses[size].circle} rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ease-in-out border border-solid ${borderColor} bg-card shadow-sm`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCalculateScore}
        style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        {isCalculating ? (
          <Loader2 className={`${sizeClasses[size].icon} text-current animate-spin`} />
        ) : (
          <>
            {score > 0 ? (
              <div className="flex items-center justify-center">
                <span className={`font-bold ${scoreColor} text-center`}>{score}</span>
                {hasIncreased && (
                  <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5 shadow-sm">
                    <FaArrowUp
                      className={`${sizeClasses[size].arrow} text-white`}
                      style={{
                        animation: isHovered ? 'pulse 1.5s infinite ease-in-out' : 'none',
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <PlusCircle className={`${sizeClasses[size].icon} ${scoreColor}`} />
            )}
          </>
        )}
      </div>

      {/* Hover tooltip */}
      {isHovered && (
        <div
          className={`absolute z-[10000] text-xs rounded-md py-1 px-2 whitespace-nowrap shadow-lg dark:shadow-black/50 dark:bg-zinc-900 dark:border dark:border-white/10 ${
            hasIncreased
              ? 'bg-gradient-to-r from-green-500 to-green-600 text-white dark:bg-none dark:text-green-400'
              : score > 0
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white dark:bg-none dark:text-blue-400'
                : 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white dark:bg-none dark:text-indigo-400'
          }`}
          style={{
            top: '50%',
            left: '100%',
            transform: 'translateY(-50%) translateX(8px)',
            opacity: 0,
            animation: 'fadeInRight 0.3s forwards',
            pointerEvents: 'none', // Prevent tooltip from interfering with other elements
          }}
        >
          <style>
            {`
              @keyframes fadeInRight {
                from { opacity: 0; transform: translateY(-50%) translateX(12px); }
                to { opacity: 1; transform: translateY(-50%) translateX(8px); }
              }
              @keyframes pulse {
                0% { transform: scale(1); }
                50% { transform: scale(1.2); }
                100% { transform: scale(1); }
              }
            `}
          </style>
          {/* Triangle pointer for the tooltip */}
          <div
            className={`absolute w-0 h-0 border-y-[5px] border-y-transparent border-r-[5px] ${
              hasIncreased
                ? 'border-r-green-500 dark:border-r-zinc-900'
                : score > 0
                  ? 'border-r-blue-500 dark:border-r-zinc-900'
                  : 'border-r-indigo-500 dark:border-r-zinc-900'
            }`}
            style={{
              top: '50%',
              left: '-4px',
              transform: 'translateY(-50%)',
            }}
          />
          {getHoverText()}
        </div>
      )}
    </div>
  );
};

export default CircularLeadScoreDisplay;
